import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_API_URL || '/api';

/**
 * Custom hook to check payment settings and permissions
 * Returns payment configuration and whether the current user can process payments
 */
const usePaymentSettings = () => {
  const { isAdmin, isGroomer } = useAuth();
  const [paymentSettings, setPaymentSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadPaymentSettings();
  }, []);

  const loadPaymentSettings = async () => {
    try {
      const token = sessionStorage.getItem('token');
      const response = await axios.get(`${API_URL}/payment-settings/active`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPaymentSettings(response.data);
      setError(null);
    } catch (err) {
      console.error('Failed to load payment settings:', err);
      setError(err);
      // Default to cash only if settings can't be loaded
      setPaymentSettings({
        provider: 'cash_only',
        is_enabled: true,
        online_payments_enabled: false,
        allow_client_payments: false,
        allow_employee_payments: false,
        allow_admin_payments: false,
      });
    } finally {
      setLoading(false);
    }
  };

  /**
   * Check if the current user can process payments based on their role
   */
  const canProcessPayments = () => {
    if (!paymentSettings || !paymentSettings.online_payments_enabled) {
      return false;
    }

    // Admin check
    if (isAdmin && paymentSettings.allow_admin_payments) {
      return true;
    }

    // Employee/Groomer check
    if (isGroomer && paymentSettings.allow_employee_payments) {
      return true;
    }

    // Client check (user is not admin or groomer)
    if (!isAdmin && !isGroomer && paymentSettings.allow_client_payments) {
      return true;
    }

    return false;
  };

  /**
   * Check if payment is required at booking time
   */
  const isPaymentRequired = () => {
    return paymentSettings?.require_payment_upfront || false;
  };

  /**
   * Check if down payments (a.k.a. partial payment at booking) are allowed.
   * Backed by the same `allow_partial_payment` column for storage compat.
   */
  const allowsDownPayment = () => {
    return paymentSettings?.allow_partial_payment || false;
  };
  // Backward-compat alias: callers that still say "partial payment" / "deposit".
  const allowsPartialPayment = allowsDownPayment;

  /**
   * Down-payment type: 'percent' (default) or 'fixed'. Controls whether the
   * down-payment value is interpreted as a percentage of the total or as a
   * fixed dollar amount.
   */
  const getDownPaymentType = () => {
    return paymentSettings?.down_payment_type || 'percent';
  };

  /**
   * Down-payment percentage (used when type === 'percent').
   */
  const getDownPaymentPercentage = () => {
    return paymentSettings?.partial_payment_percentage || 50;
  };

  /**
   * Down-payment fixed dollar amount (used when type === 'fixed').
   */
  const getDownPaymentFixedAmount = () => {
    return parseFloat(paymentSettings?.down_payment_fixed_amount || 0);
  };

  /**
   * Resolve the actual down-payment dollar amount for a given total. Honours
   * the configured type. Caps at total so a misconfigured fixed amount can
   * never exceed the bill.
   */
  const computeDownPaymentAmount = (totalPrice) => {
    const total = parseFloat(totalPrice) || 0;
    if (!allowsDownPayment() || total <= 0) return 0;
    if (getDownPaymentType() === 'fixed') {
      return Math.min(getDownPaymentFixedAmount(), total);
    }
    return Math.min((total * getDownPaymentPercentage()) / 100, total);
  };

  /**
   * Whether admin can override the per-appointment down-payment amount
   * before sending the payment link to the client.
   */
  const adminCanOverrideDownPayment = () => {
    return paymentSettings?.down_payment_admin_can_override || false;
  };

  /**
   * Client-facing non-refundable notice. Falls back to a sensible default
   * if the admin hasn't customized one.
   */
  const getDownPaymentNotice = () => {
    const custom = paymentSettings?.down_payment_notice;
    if (custom && custom.trim()) return custom.trim();
    return 'Down payments are non-refundable. They are paid ahead of time and do not come back to you.';
  };

  /**
   * Whether clients are allowed to split their full payment into chunks
   * (installments) at checkout.
   */
  const allowsInstallments = () => {
    return paymentSettings?.allow_installments || false;
  };

  /**
   * Maximum number of installment chunks the admin allows.
   */
  const getInstallmentsMaxCount = () => {
    const v = parseInt(paymentSettings?.installments_max_count, 10);
    return Number.isFinite(v) && v >= 2 ? v : 4;
  };

  // Backward-compat alias for existing BookAppointment.js code that still
  // reads depositPercentage. Maps to the new percent value.
  const getDepositPercentage = getDownPaymentPercentage;

  /**
   * Get the active payment provider (legacy - returns first provider)
   */
  const getProvider = () => {
    return paymentSettings?.provider || 'cash_only';
  };

  /**
   * Get all active payment providers
   */
  const getProviders = () => {
    return paymentSettings?.providers || [];
  };

  /**
   * Check if online payments are enabled at all
   */
  const isOnlinePaymentEnabled = () => {
    return paymentSettings?.online_payments_enabled || false;
  };

  return {
    paymentSettings,
    loading,
    error,
    canProcessPayments: canProcessPayments(),
    isPaymentRequired: isPaymentRequired(),
    // New down-payment surface
    allowsDownPayment: allowsDownPayment(),
    downPaymentType: getDownPaymentType(),
    downPaymentPercentage: getDownPaymentPercentage(),
    downPaymentFixedAmount: getDownPaymentFixedAmount(),
    computeDownPaymentAmount,
    adminCanOverrideDownPayment: adminCanOverrideDownPayment(),
    downPaymentNotice: getDownPaymentNotice(),
    // Installments
    allowsInstallments: allowsInstallments(),
    installmentsMaxCount: getInstallmentsMaxCount(),
    // Backward-compat aliases
    allowsPartialPayment: allowsPartialPayment(),
    depositPercentage: getDepositPercentage(),
    provider: getProvider(), // Legacy - first provider
    providers: getProviders(), // Array of all active providers
    isOnlinePaymentEnabled: isOnlinePaymentEnabled(),
    reload: loadPaymentSettings,
  };
};

export default usePaymentSettings;
